/**
 * <newsletter-branch-picker>
 *
 * Optional "Branch(es) of Service" pop-up for the footer newsletter signup.
 *
 * Progressive enhancement: the footer form (snippets/newsletter-signup.liquid) is a normal
 * Shopify customer form that works without this script. When the script runs, tapping the
 * email field opens a <dialog> holding the email field and the branch checkboxes. Submitting the
 * dialog copies the email and the chosen branches into the real form's `contact[email]` and
 * `contact[tags]` fields, then submits that form once.
 *
 * The tags sent are the form's existing tags (for example "prospect, newsletter") followed by
 * the chosen branch names. Choosing no branch sends exactly what the form sent before.
 */
class NewsletterBranchPicker extends HTMLElement {
  connectedCallback() {
    // The element is moved to <body> once, which calls connectedCallback again.
    if (this.initialised) return;

    this.dialog = this.querySelector('dialog');
    this.form = document.getElementById(this.dataset.form);
    this.email = document.getElementById(this.dataset.input);
    this.tagsInput = this.form && this.form.querySelector('input[name="contact[tags]"]');

    // Without <dialog> support, or if the form is missing, leave the form as a normal form.
    if (!this.dialog || !this.form || !this.email || !this.tagsInput || typeof this.dialog.showModal !== 'function') return;

    this.initialised = true;
    this.baseTags = this.tagsInput.value;
    this.panel = this.dialog.querySelector('.nbp__form');
    this.dialogEmail = this.dialog.querySelector('.nbp__input');
    this.error = this.dialog.querySelector('.nbp__error');
    this.submitButton = this.dialog.querySelector('.nbp__submit');

    // Keep the dialog out of the footer so footer styles do not apply to it.
    document.body.appendChild(this);

    // A read-only field keeps the on-screen keyboard closed on phones until the dialog opens.
    this.email.readOnly = true;
    this.email.setAttribute('aria-haspopup', 'dialog');
    this.email.classList.add('nbp-trigger');

    this.onTriggerClick = () => this.open();
    this.onTriggerKeydown = (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const isCharacter = event.key.length === 1 && event.key !== ' ';
      if (isCharacter || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.open(isCharacter ? event.key : '');
      }
    };
    // The arrow button, or any other way of submitting the footer form, opens the dialog first.
    this.onFormSubmit = (event) => {
      if (this.submitting) return;
      event.preventDefault();
      this.open();
    };
    this.onPageShow = (event) => {
      if (event.persisted) this.reset();
    };
    this.onSectionUnload = () => {
      if (!this.form.isConnected) this.remove();
    };

    this.email.addEventListener('click', this.onTriggerClick);
    this.email.addEventListener('keydown', this.onTriggerKeydown);
    this.form.addEventListener('submit', this.onFormSubmit);
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('shopify:section:unload', this.onSectionUnload);

    this.panel.addEventListener('submit', (event) => this.onSubmit(event));
    this.dialogEmail.addEventListener('input', () => this.showError(false));
    this.dialog.addEventListener('cancel', () => this.onClose());
    this.dialog.addEventListener('close', () => this.onClose());
    this.dialog.addEventListener('click', (event) => {
      // A click on the backdrop lands on the <dialog> itself, not on the panel inside it.
      if (event.target === this.dialog) this.closeDialog();
    });
    this.dialog.querySelectorAll('[data-nbp-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeDialog());
    });
  }

  disconnectedCallback() {
    this.removeListeners();
  }

  removeListeners() {
    if (!this.email) return;
    this.email.removeEventListener('click', this.onTriggerClick);
    this.email.removeEventListener('keydown', this.onTriggerKeydown);
    this.form.removeEventListener('submit', this.onFormSubmit);
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('shopify:section:unload', this.onSectionUnload);
  }

  open(prefill = '') {
    if (this.dialog.open) return;

    // Always start with no branches chosen, whatever happened the last time it was open.
    this.clearChoices();
    this.dialogEmail.value = this.email.value || prefill;
    this.dialog.showModal();
    document.documentElement.classList.add('nbp-open');

    // On touch screens leave the keyboard closed so it does not cover the branch choices.
    if (window.matchMedia('(pointer: coarse)').matches) {
      this.dialog.focus();
    } else {
      this.dialogEmail.focus();
      // Email fields do not support setSelectionRange(); re-setting the value puts the caret at the end.
      const typed = this.dialogEmail.value;
      this.dialogEmail.value = '';
      this.dialogEmail.value = typed;
    }
  }

  // Closes the dialog and undoes everything open() did. Also runs from the dialog's own
  // `close` event, so the Escape key is covered too.
  closeDialog() {
    if (this.dialog.open) this.dialog.close();
    this.onClose();
  }

  // Branch choices are never kept: every time the dialog closes (submit, "No thanks", the close
  // button, the backdrop or Escape) they are cleared, so the next opening starts empty.
  clearChoices() {
    this.dialog.querySelectorAll('input[name="branch"]').forEach((box) => { box.checked = false; });
    this.showError(false);
  }

  onClose() {
    this.clearChoices();
    const wasLocked = document.documentElement.classList.contains('nbp-open');
    document.documentElement.classList.remove('nbp-open');
    if (wasLocked && !this.submitting) this.email.focus({ preventScroll: true });
  }

  showError(visible) {
    this.error.hidden = !visible;
    if (visible) {
      this.dialogEmail.setAttribute('aria-invalid', 'true');
    } else {
      this.dialogEmail.removeAttribute('aria-invalid');
    }
  }

  onSubmit(event) {
    event.preventDefault();

    const value = this.dialogEmail.value.trim();
    this.dialogEmail.value = value;
    if (!value || !this.dialogEmail.checkValidity()) {
      this.showError(true);
      this.dialogEmail.focus();
      return;
    }

    const branches = Array.from(this.dialog.querySelectorAll('input[name="branch"]:checked')).map((box) => box.value);

    this.email.value = value;
    this.tagsInput.value = [this.baseTags].concat(branches).filter(Boolean).join(', ');

    // Shopify protects the signup form with a CAPTCHA that its own script adds on a normal form
    // submit, so the form is submitted normally. The page reloads and the footer shows Shopify's
    // confirmation message.
    this.submitting = true;
    this.submitButton.disabled = true;
    this.closeDialog();
    this.showPending();

    if (typeof this.form.requestSubmit === 'function') {
      this.form.requestSubmit();
    } else {
      this.form.submit();
    }
  }

  // While the form is being sent and the page reloads, show a "Subscribing..." spinner in the
  // spot where the confirmation will appear, so it is clear something is happening and the
  // customer does not tap Subscribe again.
  showPending() {
    this.hidePending();
    this.pending = document.createElement('p');
    this.pending.className = 'nbp-pending';
    this.pending.setAttribute('role', 'status');
    const text = document.createElement('span');
    text.textContent = this.dataset.pending;
    this.pending.appendChild(text);
    this.form.appendChild(this.pending);

    // A second signup on the same page: the earlier confirmation steps aside while this one is sent.
    this.hiddenAlerts = Array.from(this.form.querySelectorAll('.alert')).filter((alert) => !alert.hidden);
    this.hiddenAlerts.forEach((alert) => { alert.hidden = true; });

    this.form.setAttribute('aria-busy', 'true');
    this.formButtons = Array.from(this.form.querySelectorAll('button'));
    this.formButtons.forEach((button) => { button.disabled = true; });

    // If the page never reloads (for example a CAPTCHA that is not completed), do not leave a
    // spinner running for ever: put the form back after a while.
    this.pendingTimer = setTimeout(() => this.reset(), 20000);
  }

  hidePending() {
    clearTimeout(this.pendingTimer);
    if (this.pending) this.pending.remove();
    this.pending = null;
    this.form.removeAttribute('aria-busy');
    (this.formButtons || []).forEach((button) => { button.disabled = false; });
    this.formButtons = [];
    (this.hiddenAlerts || []).forEach((alert) => { alert.hidden = false; });
    this.hiddenAlerts = [];
  }

  // Restores the form when the page comes back from the browser's back/forward cache.
  reset() {
    this.submitting = false;
    this.submitButton.disabled = false;
    this.tagsInput.value = this.baseTags;
    this.hidePending();
    this.clearChoices();
  }
}

if (!customElements.get('newsletter-branch-picker')) {
  customElements.define('newsletter-branch-picker', NewsletterBranchPicker);
}

// Shopify shows its "Thank you for subscribing." message only while the page address contains
// ?customer_posted=true, so a refresh would show it again every time. Once the page has rendered,
// bring the message into view (so a customer notices it) and remove that one parameter from the
// address, so the next refresh gives a clean footer.
(function showSignupConfirmation() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('customer_posted')) return;

    const message = document.querySelector('.usamm-footer__newsletter .alert');
    if (message) {
      // Images keep loading and moving the footer after the page first appears, so check again
      // as the page settles. Stop as soon as the customer scrolls or touches the page themselves.
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let customerMovedPage = false;
      ['wheel', 'touchstart', 'mousedown', 'keydown'].forEach((type) => {
        window.addEventListener(type, () => { customerMovedPage = true; }, { once: true, passive: true });
      });
      const isInView = () => {
        const box = message.getBoundingClientRect();
        return box.top >= 80 && box.bottom <= window.innerHeight - 24;
      };
      const reveal = (behavior) => {
        if (customerMovedPage || isInView()) return;
        message.scrollIntoView({ block: 'center', behavior });
      };
      reveal(reduceMotion ? 'auto' : 'smooth');
      window.addEventListener('load', () => reveal('auto'));
      // Keep checking for a few seconds while the page above the footer is still changing height.
      const check = setInterval(() => reveal('auto'), 250);
      setTimeout(() => clearInterval(check), 5000);
    }

    url.searchParams.delete('customer_posted');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  } catch (error) {
    // Leaving the address alone is harmless; the message just stays until the address changes.
  }
}());
